/**
 * Requests V2 Type Definitions
 *
 * Canonical types for the Requests (Care Requests) module.
 * These align with the v2 schema after migrations have run.
 */

// ── Status & Enum Types ────────────────────────────────────────────────

export type RequestStatus = 'open' | 'in_progress' | 'waiting' | 'closed' | 'resolved';
export type RequestPriority = 'low' | 'normal' | 'high';
export type RequestChannel = 'member_portal' | 'staff' | 'kiosk' | 'import' | 'api';
export type RequestCategory = 'care' | 'facility' | 'admin' | 'other' | 'reimbursement';
export type RequestLane = 'care' | 'operations' | 'finance';

/**
 * Explicit operational groupings for UI filters.
 * These map to existing request.category enum values during transition.
 */
export type OperationalRequestCategory = 'facility' | 'audiovisual_tech' | 'finance_reimbursement' | 'admin';
export type RequestCategoryFilter = RequestCategory | OperationalRequestCategory;

// ── Option Arrays for Dropdowns ────────────────────────────────────────

export const REQUEST_STATUS_OPTIONS: { value: RequestStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

export const REQUEST_PRIORITY_OPTIONS: { value: RequestPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
];

export const REQUEST_CHANNEL_OPTIONS: { value: RequestChannel; label: string }[] = [
  { value: 'member_portal', label: 'Member Portal' },
  { value: 'staff', label: 'Staff' },
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'import', label: 'Import' },
  { value: 'api', label: 'API' },
];

export const REQUEST_CATEGORY_OPTIONS: { value: RequestCategory; label: string }[] = [
  { value: 'care', label: 'Care' },
  { value: 'facility', label: 'Facility' },
  { value: 'admin', label: 'Admin' },
  { value: 'reimbursement', label: 'Reimbursement' },
  { value: 'other', label: 'Other' },
];

export const REQUEST_LANE_OPTIONS: { value: RequestLane; label: string }[] = [
  { value: 'care', label: 'Care' },
  { value: 'operations', label: 'Operations' },
  { value: 'finance', label: 'Finance' },
];

export const OPERATIONAL_CATEGORY_OPTIONS: { value: OperationalRequestCategory; label: string }[] = [
  { value: 'facility', label: 'Facility' },
  { value: 'audiovisual_tech', label: 'Audiovisual / Tech' },
  { value: 'finance_reimbursement', label: 'Finance / Reimbursement' },
  { value: 'admin', label: 'Admin' },
];

export const OPERATIONAL_CATEGORY_TO_REQUEST_CATEGORIES: Record<OperationalRequestCategory, RequestCategory[]> = {
  facility: ['facility'],
  audiovisual_tech: ['facility'],
  finance_reimbursement: ['reimbursement', 'other'],
  admin: ['admin'],
};

export const CARE_LANE_DEFAULT_CATEGORIES: RequestCategory[] = ['care'];
export const OPERATIONS_LANE_DEFAULT_CATEGORIES: RequestCategory[] = ['facility', 'admin', 'reimbursement', 'other'];
export const FINANCE_LANE_DEFAULT_CATEGORIES: RequestCategory[] = ['reimbursement'];

export const REQUEST_CHANNEL_LABELS: Record<RequestChannel, string> = {
  member_portal: 'Member Portal',
  staff: 'Staff',
  kiosk: 'Kiosk',
  import: 'Import',
  api: 'API',
};

export const REQUEST_CATEGORY_LABELS: Record<RequestCategory, string> = {
  care: 'Care',
  facility: 'Facility',
  admin: 'Admin',
  reimbursement: 'Reimbursement',
  other: 'Other',
};

// ── V2 Status Config (for badges/chips) ────────────────────────────────

export const REQUEST_V2_STATUS_CONFIG: Record<RequestStatus, { label: string; tone: 'info' | 'warning' | 'neutral' | 'success' }> = {
  open: { label: 'Open', tone: 'info' },
  in_progress: { label: 'In Progress', tone: 'warning' },
  waiting: { label: 'Waiting', tone: 'neutral' },
  resolved: { label: 'Resolved', tone: 'success' },
  closed: { label: 'Closed', tone: 'success' },
};

export const REQUEST_V2_PRIORITY_CONFIG: Record<RequestPriority, { label: string; tone: 'neutral' | 'info' | 'warning' }> = {
  low: { label: 'Low', tone: 'neutral' },
  normal: { label: 'Normal', tone: 'info' },
  high: { label: 'High', tone: 'warning' },
};

// ── Care-Specific Enums ───────────────────────────────────────────────

export type CareRequestType = 'hospital_visit' | 'counseling' | 'follow_up' | 'prayer' | 'other' | 'visit' | 'meal' | 'benevolence';
export type ContactMethod = 'phone' | 'email' | 'sms' | 'in_person' | 'any';
export type VisibilityLevel = 'normal' | 'private' | 'restricted' | 'clergy_only' | 'ministry_team' | 'prayer_list';

export const CARE_TYPE_OPTIONS: { value: CareRequestType; label: string }[] = [
  { value: 'prayer', label: 'Prayer request' },
  { value: 'hospital_visit', label: 'Hospital visit' },
  { value: 'counseling', label: 'Counseling' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'visit', label: 'Home / offsite visit' },
  { value: 'meal', label: 'Meal' },
  { value: 'benevolence', label: 'Benevolence' },
  { value: 'other', label: 'Other' },
];

export const CARE_TYPE_LABELS: Record<string, string> = {
  hospital_visit: 'Hospital visit',
  counseling: 'Counseling',
  follow_up: 'Follow-up',
  prayer: 'Prayer',
  visit: 'Home / offsite visit',
  meal: 'Meal',
  benevolence: 'Benevolence',
  other: 'Other',
};

export const CONTACT_METHOD_OPTIONS: { value: ContactMethod; label: string }[] = [
  { value: 'any', label: 'Any method' },
  { value: 'phone', label: 'Phone call' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'Text message' },
  { value: 'in_person', label: 'In person' },
];

export const CONTACT_METHOD_LABELS: Record<ContactMethod, string> = {
  any: 'Any method',
  phone: 'Phone call',
  email: 'Email',
  sms: 'Text message',
  in_person: 'In person',
};

export const VISIBILITY_OPTIONS: { value: VisibilityLevel; label: string; description: string }[] = [
  { value: 'normal', label: 'Normal', description: 'Visible to all staff' },
  { value: 'private', label: 'Private', description: 'Only assigned owner' },
  { value: 'restricted', label: 'Restricted', description: 'Leadership only' },
  { value: 'clergy_only', label: 'Clergy only', description: 'Ordained clergy only' },
  { value: 'ministry_team', label: 'Ministry team', description: 'Pastoral care team' },
  { value: 'prayer_list', label: 'Prayer list', description: 'Added to congregation prayer list' },
];

export const VISIBILITY_LABELS: Record<string, string> = {
  normal: 'Normal',
  private: 'Private',
  restricted: 'Restricted',
  clergy_only: 'Clergy only',
  ministry_team: 'Ministry team',
  prayer_list: 'Prayer list',
};

// ── Reimbursement-Specific Types ────────────────────────────────────────

export type ReimbursementPaymentMethod = 'personal_credit_card' | 'personal_debit_card' | 'cash' | 'personal_check' | 'other';

export const REIMBURSEMENT_PAYMENT_METHOD_OPTIONS: { value: ReimbursementPaymentMethod; label: string }[] = [
  { value: 'personal_credit_card', label: 'Personal credit card' },
  { value: 'personal_debit_card', label: 'Personal debit card' },
  { value: 'cash', label: 'Cash' },
  { value: 'personal_check', label: 'Personal check' },
  { value: 'other', label: 'Other' },
];

// ── Database Row Interface ─────────────────────────────────────────────

export interface RequestV2 {
  id: string;
  organization_id: string;
  summary: string;
  details: string | null;
  status: RequestStatus;
  priority: RequestPriority;
  category: RequestCategory;
  channel: RequestChannel;
  triage_owner_id: string | null;
  assigned_team_id: string | null;
  requester_person_id: string | null;
  requester_name: string | null;
  requester_contact: string | null;
  person_id: string | null;
  visibility: string | null;
  received_at: string;
  due_at: string | null;
  closed_at: string | null;
  resolved_at: string | null;
  outcome: string | null;
  created_at: string;
  updated_at: string;
  // Care-specific fields
  household_id: string | null;
  care_type: CareRequestType | null;
  preferred_contact_method: ContactMethod | null;
  notes: string | null;
  urgency: string | null;
  add_to_prayer_list: boolean;
  location: string | null;
  location_notes: string | null;
  follow_up_date: string | null;
  follow_up_owner_id: string | null;
  claimed_by_id: string | null;
  claimed_at: string | null;
  escalated_at: string | null;
  escalated_to_id: string | null;
  team_id: string | null;
  // Reimbursement-specific fields
  reimbursement_amount: number | null;
  reimbursement_vendor: string | null;
  reimbursement_purchase_date: string | null;
  expense_category_id: string | null;
  reimbursement_payment_method: ReimbursementPaymentMethod | null;
}

/** Extended request with joined relations from Supabase queries */
export interface RequestV2WithDetails extends RequestV2 {
  requester_person?: {
    id: string;
    first_name: string;
    last_name: string;
    photo_url?: string | null;
    phone?: string | null;
    email?: string | null;
  } | null;
  triage_owner?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  household?: {
    id: string;
    name: string;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null;
  linked_tasks_count?: number;
}

// ── Mutation Payloads ──────────────────────────────────────────────────

export interface CreateRequestV2Payload {
  organization_id: string;
  summary: string;
  details?: string;
  category: RequestCategory;
  channel?: RequestChannel;
  priority?: RequestPriority;
  requester_person_id?: string;
  requester_name?: string;
  requester_contact?: string;
  triage_owner_id?: string;
  due_at?: string;
  // Care-specific fields
  visibility?: string;
  household_id?: string;
  care_type?: CareRequestType;
  preferred_contact_method?: ContactMethod;
  notes?: string;
  urgency?: string;
  add_to_prayer_list?: boolean;
  location?: string;
  location_notes?: string;
  // Reimbursement-specific fields
  reimbursement_amount?: number;
  reimbursement_vendor?: string;
  reimbursement_purchase_date?: string;
  expense_category_id?: string;
  reimbursement_payment_method?: ReimbursementPaymentMethod;
}

export interface UpdateRequestV2Payload {
  id: string;
  status?: RequestStatus;
  /** Set on close and cleared on any other status unless given; undo passes the previous value. */
  closed_at?: string | null;
  triage_owner_id?: string | null;
  priority?: RequestPriority;
  due_at?: string | null;
  outcome?: string;
  category?: RequestCategory;
  summary?: string;
  details?: string;
  // Care-specific fields
  visibility?: string | null;
  household_id?: string | null;
  care_type?: CareRequestType | null;
  preferred_contact_method?: ContactMethod | null;
  notes?: string | null;
  urgency?: string | null;
  add_to_prayer_list?: boolean;
  location?: string | null;
  location_notes?: string | null;
}

// ── Link Table ─────────────────────────────────────────────────────────

export interface RequestTaskLink {
  id: string;
  request_id: string;
  task_id: string;
  created_at: string;
  created_by: string | null;
}

// ── Filters ────────────────────────────────────────────────────────────

export interface RequestV2Filters {
  status?: RequestStatus | RequestStatus[];
  category?: RequestCategory | RequestCategory[];
  priority?: RequestPriority;
  triage_owner_id?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}
